import { format } from "date-fns"
import { Navigate, useSearchParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import { ACTIVITY_REPORT_URL_RANGE } from "../activity-report-logs/reportDateRangePresets"
import { adminApi } from "../axios"
import AdminExportCsvButton from "../components/AdminExportCsvButton"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar, {
  dateRangeToParams,
} from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"

const EVENT_TYPES = [
  "",
  "login",
  "logout",
  "account_switch",
  "assignment_created",
  "assignment_activated",
  "assignment_deactivated",
  "assignment_revoked",
  "calling_number_assigned",
  "calling_number_cleared",
  "access_denied",
  "high_risk_action",
]

function formatEventType(value) {
  return String(value || "").replace(/_/g, " ")
}

export const AUDIT_LOG_FILTER_SPECS = {
  event: { param: "event_type" },
  concierge: { param: "concierge_user_id" },
  account: { param: "account_owner_id" },
  created: { toParams: dateRangeToParams("created") },
}

const TOOLBAR_FILTERS = [
  {
    key: "event",
    label: "Event type",
    type: "select",
    anyLabel: "All event types",
    minWidth: 200,
    options: EVENT_TYPES.filter(Boolean).map((type) => ({
      value: type,
      label: formatEventType(type),
    })),
  },
  { key: "concierge", label: "Concierge", type: "entity", entity: "concierge" },
  {
    key: "account",
    label: "Account owner",
    type: "entity",
    entity: "account",
    placeholder: "Type 2+ letters",
  },
  { key: "created", label: "When", type: "daterange" },
]

function AdminAuditLogsContent() {
  const query = useAdminPaginatedQuery(adminApi.fetchAuditLogs, {
    filters: AUDIT_LOG_FILTER_SPECS,
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    exportParams,
  } = query

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Concierge audit logs"
        subtitle="Security events for concierge operators."
      />
      <AdminListToolbar
        query={query}
        searchLabel="Search logs"
        noun={totalResultsSize === 1 ? "event" : "events"}
        filters={TOOLBAR_FILTERS}
        actions={
          <AdminExportCsvButton
            exportFn={adminApi.exportAuditLogs}
            params={exportParams}
            filenamePrefix="concierge-audit-logs"
          />
        }
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={5}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="created"
              label="When"
              query={query}
              minWidth={{ xs: 72, sm: 88 }}
            />
            <AdminSortableHeaderCell
              columnName="event_type"
              label="Event"
              query={query}
              minWidth={{ xs: 64, sm: 80 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 112 }}>
              Concierge
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Account owner
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
              IP
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <AdminTruncatedCell
              value={
                row.created
                  ? format(new Date(row.created), "MMM d, yyyy h:mm a")
                  : "—"
              }
            />
            <AdminTruncatedCell value={formatEventType(row.event_type)} />
            <AdminTruncatedCell
              value={row.concierge_name || row.concierge_email}
            />
            <AdminTruncatedCell
              value={row.account_owner_name || row.account_owner_email || "—"}
            />
            <AdminTruncatedCell value={row.ip_address} />
          </AdminTableRow>
        ))}
      </AdminListingTable>
    </Column>
  )
}

export default function AdminAuditLogs() {
  const [searchParams] = useSearchParams()

  if (searchParams.get("tab") === "activity-reports") {
    const params = new URLSearchParams()
    params.set("range", ACTIVITY_REPORT_URL_RANGE.last30Days)
    const reportDate = searchParams.get("report_date")
    if (reportDate) {
      params.delete("range")
      params.set("report_date", reportDate)
    }
    return (
      <Navigate
        to={`/admin/activity-report-emails?${params.toString()}`}
        replace
      />
    )
  }

  return <AdminAuditLogsContent />
}
